/**
 * Pure mass-spring dynamics for the hero-icons stage.
 *
 * A third additive layer on top of `bigBang` and `ambient` (see
 * `motion.ts`). The integrator is plain semi-implicit Euler with
 * exponential damping and a fixed substep count for stability when the
 * host's `dt` spikes (tab blur recovery, slow frames). All positions are
 * tracked as DISPLACEMENTS from the asset's resting position, in
 * container pixels — so summing into the existing offsets map in
 * `useHeroMotion` is a no-op composition.
 *
 * The hook in `use-hero-motion.ts` owns the loop and pointer events; this
 * module is a pure transformation of state with no React or DOM
 * dependencies.
 */
import type { HeroAsset, IconEdge } from './layout';

/** Per-asset dynamics state, mutated in place by `integrate`. */
export type AssetDynState = {
	/** Displacement from rest in stage pixels. */
	dispX: number;
	dispY: number;
	/** Velocity in stage pixels / second. */
	vx: number;
	vy: number;
	/** When true, this asset is pinned by the pointer; forces are ignored. */
	dragging: boolean;
};

export type DynamicsCfg = {
	/** Self spring stiffness toward rest. Units: 1/s^2. */
	kSelf: number;
	/** Edge spring stiffness coupling neighbours. Units: 1/s^2. */
	kEdge: number;
	/**
	 * Exponential damping coefficient (1/s). Velocity decays as
	 * `v *= exp(-damping * dt)` per substep — stable for any dt and damping.
	 */
	damping: number;
	/** Hard cap on integrated dt per frame so spikes can't explode the sim. */
	maxDt: number;
	/** Number of fixed substeps per frame (>=1). */
	substeps: number;
};

export const DEFAULT_DYNAMICS_CFG: DynamicsCfg = {
	kSelf: 80,
	kEdge: 20,
	damping: 8,
	maxDt: 1 / 30,
	substeps: 2,
};

/** Threshold (px and px/s) below which we snap to rest to avoid jitter. */
const REST_EPSILON = 0.01;

export type DynStateMap = Map<string, AssetDynState>;

/** Build an empty dyn state entry for every asset. */
export const createDynState = (assets: readonly HeroAsset[]): DynStateMap => {
	const out: DynStateMap = new Map();
	for (const a of assets) {
		out.set(a.id, { dispX: 0, dispY: 0, vx: 0, vy: 0, dragging: false });
	}
	return out;
};

/** Zero displacement and velocity for every entry (preserves dragging flags). */
export const resetDynState = (state: DynStateMap): void => {
	for (const s of state.values()) {
		s.dispX = 0;
		s.dispY = 0;
		s.vx = 0;
		s.vy = 0;
	}
};

/**
 * Adjacency built from the edge list. Stored as parallel arrays of
 * `(state, state)` references so the inner integration loop avoids any
 * map lookups per substep.
 */
export type Adjacency = {
	a: AssetDynState[];
	b: AssetDynState[];
};

export const buildAdjacency = (state: DynStateMap, edges: readonly IconEdge[]): Adjacency => {
	const a: AssetDynState[] = [];
	const b: AssetDynState[] = [];
	for (const e of edges) {
		const sa = state.get(e.from);
		const sb = state.get(e.to);
		if (!sa || !sb) continue;
		a.push(sa);
		b.push(sb);
	}
	return { a, b };
};

/**
 * Integrate the spring system by `dt` seconds. Mutates `state` in place.
 *
 * Algorithm per substep (`h = dt / substeps`):
 *   1. Self spring  : v += -kSelf * disp * h        (skip if dragging)
 *   2. Edge springs : f = -kEdge * (disp_a - disp_b) * h
 *                     apply +f to a, -f to b        (skip dragged ends)
 *   3. Damp         : v *= exp(-damping * h)
 *   4. Integrate    : disp += v * h
 *
 * Dragged nodes have their displacement written directly by the pointer
 * handler; we leave their dispX/dispY/vx/vy alone here.
 */
export const integrate = (
	state: DynStateMap,
	adj: Adjacency,
	cfg: DynamicsCfg,
	dt: number,
): void => {
	if (dt <= 0) return;
	const stepDt = Math.min(dt, cfg.maxDt) / Math.max(cfg.substeps, 1);
	const decay = Math.exp(-Math.max(cfg.damping, 0) * stepDt);
	const kSelfH = cfg.kSelf * stepDt;
	const kEdgeH = cfg.kEdge * stepDt;

	for (let step = 0; step < cfg.substeps; step++) {
		if (kSelfH > 0) {
			for (const s of state.values()) {
				if (s.dragging) continue;
				s.vx -= kSelfH * s.dispX;
				s.vy -= kSelfH * s.dispY;
			}
		}

		if (kEdgeH > 0) {
			const { a, b } = adj;
			for (let i = 0; i < a.length; i++) {
				const sa = a[i];
				const sb = b[i];
				const fx = -kEdgeH * (sa.dispX - sb.dispX);
				const fy = -kEdgeH * (sa.dispY - sb.dispY);
				if (!sa.dragging) {
					sa.vx += fx;
					sa.vy += fy;
				}
				if (!sb.dragging) {
					sb.vx -= fx;
					sb.vy -= fy;
				}
			}
		}

		for (const s of state.values()) {
			if (s.dragging) continue;
			s.vx *= decay;
			s.vy *= decay;
			s.dispX += s.vx * stepDt;
			s.dispY += s.vy * stepDt;

			if (
				Math.abs(s.dispX) < REST_EPSILON &&
				Math.abs(s.dispY) < REST_EPSILON &&
				Math.abs(s.vx) < REST_EPSILON &&
				Math.abs(s.vy) < REST_EPSILON
			) {
				s.dispX = 0;
				s.dispY = 0;
				s.vx = 0;
				s.vy = 0;
			}
		}
	}
};
